import * as React from 'react';
import styles from './index.less';
import FComponentsLib from '@freelog/components-lib';
import { FI18n, FServiceAPI, FUtil } from '@freelog/tools-lib';
import * as AHooks from 'ahooks';
import FHotspotTooltip, { fSetHotspotTooltipVisible } from '@/components/FHotspotTooltip';
import FResourceTypeInput4 from '@/components/FResourceTypeInput4';
import FIcons from '@/components/FIcons';
import { FRegExpMgr } from '@/utils/FRegExpMgr';
import fMessage from '@/components/fMessage';
import { history, useParams } from 'umi';
import FPrompt from '@/components/FPrompt';
import FInput_PinyinSafeTextCounter from '@/components/FInput_PinyinSafeTextCounter';

interface CreatorProps {}

function Creator(_props: CreatorProps) {
  const params = useParams<{ nodeID: string }>();
  const [$nodeInfo, set$nodeInfo, get$nodeInfo] = FUtil.Hook.useGetState<{
    nodeDomain: string;
    nodeID: number;
    nodeName: string;
    nodeTitle: string;
  } | null>(null);

  const [$dataIsDirty_count, set$dataIsDirty_count, get$dataIsDirty_count] =
    FUtil.Hook.useGetState<number>(0);

  const [$resourceType, set$resourceType, get$resourceType] = FUtil.Hook.useGetState<{
    value: string;
    labels: string[];
    customInput?: string;
  } | null>(null);
  const [$resourceType_errorText, set$resourceType_errorText, get$resourceType_errorText] =
    FUtil.Hook.useGetState<string>('');
  const [$resourceTitle, set$resourceTitle, get$resourceTitle] = FUtil.Hook.useGetState<string>('');
  const [$resourceTitle_errorText, set$resourceTitle_errorText, get$resourceTitle_errorText] =
    FUtil.Hook.useGetState<string>('');
  const [$resourceName, set$resourceName, get$resourceName] = FUtil.Hook.useGetState<string>('');
  const [$resourceName_optimized, set$resourceName_optimized, get$resourceName_optimized] =
    FUtil.Hook.useGetState<string>('');
  const [$resourceName_isVerify, set$resourceName_isVerify, get$resourceName_isVerify] =
    FUtil.Hook.useGetState<boolean>(false);
  const [$resourceName_errorText, set$resourceName_errorText, get$resourceName_errorText] =
    FUtil.Hook.useGetState<string>('');

  const { run: run_verify } = AHooks.useDebounceFn(
    async () => {
      if (get$resourceName_errorText() === '') {
        onVerifyName();
      }
    },
    {
      wait: 300,
    },
  );

  async function onVerifyName() {
    let nameErrorText: string = '';
    if (get$resourceName_optimized() === '') {
      // nameErrorText = '请输入资源授权标识';
      nameErrorText = FI18n.i18nNext.tAuto('naming_convention_exhibits_authid_required');
    } else {
      const reqParams: Parameters<typeof FServiceAPI.Exhibit.presentableDetails>[0] = {
        nodeId: Number(params.nodeID!),
        presentableName: get$resourceName_optimized(),
      };
      const { data: data_presentableDetails } = await FServiceAPI.Exhibit.presentableDetails(
        reqParams,
      );

      if (data_presentableDetails) {
        nameErrorText = '展品合集授权标识已经存在';
      }
    }
    set$resourceName_isVerify(false);
    set$resourceName_errorText(nameErrorText);
  }

  AHooks.useMount(async () => {
    const nodeID = params.nodeID;
    if (!nodeID) return;

    const reqParams: Parameters<typeof FServiceAPI.Node.details>[0] = {
      nodeId: Number(nodeID),
    };

    const {
      ret,
      errCode,
      msg,
      data: data_nodeDetails,
    }: {
      ret: number;
      errCode: number;
      msg: number;
      data: {
        nodeId: number;
        ownerUserId: number;
        status: number;
        nodeLogo: string;
        nodeName: string;
        nodeTitle: string;
        nodeThemeId: string;
        nodeDomain: string;
      };
    } = await FServiceAPI.Node.details(reqParams);

    if (data_nodeDetails?.status === -1) {
      history.replace(FUtil.LinkTo.nodeDeleted({ nodeID: data_nodeDetails.nodeId }));
      return;
    }

    set$nodeInfo({
      nodeDomain: data_nodeDetails.nodeDomain,
      nodeID: data_nodeDetails.nodeId,
      nodeName: data_nodeDetails.nodeName,
      nodeTitle: data_nodeDetails.nodeTitle,
    });
  });

  function onChangeName(name: string) {
    set$resourceName(name);
    set$resourceName_optimized(FRegExpMgr.resourceNameOptimized(name));
    let errorText: string = '';
    if (get$resourceName_optimized() === '') {
      errorText = FI18n.i18nNext.tAuto('naming_convention_exhibits_authid_required');
    }
    set$resourceName_errorText(errorText);
    set$dataIsDirty_count(get$dataIsDirty_count() + 1);

    if (errorText === '') {
      set$resourceName_isVerify(true);
      run_verify();
    } else {
      set$resourceName_isVerify(false);
    }
  }

  return (
    <>
      <FPrompt
        watch={$dataIsDirty_count !== 0}
        onOk={(locationHref) => {
          history.push(locationHref);
        }}
      />
      <div className={styles.container}>
        <div className={styles.content}>
          <div style={{ height: 35 }} />
          <div className={styles.nav}>
            <div className={styles.left}>{$nodeInfo?.nodeTitle}</div>
            <div style={{ width: 10 }} />
            <div className={styles.other}>{'>'}</div>
            <div style={{ width: 7 }} />
            <div className={styles.other}>
              {
                // 创建展品合集
                FI18n.i18nNext.tAuto('createexhibitcollection_title')
              }
            </div>
          </div>
          <div style={{ height: 35 }} />
          <div className={styles.block}>
            <FComponentsLib.FContentText
              text={
                // 合集类型
                FI18n.i18nNext.tAuto('createexhibitcollection_input_type_label')
              }
              type={'highlight'}
            />
            <div style={{ height: 5 }} />
            <FComponentsLib.FContentText
              text={
                // 选择最贴切描述此合集的类型，其它用户会通过合集类型在资源市场中寻找他们想要的合集。
                FI18n.i18nNext.tAuto('createexhibitcollection_input_type_info')
              }
              type={'additional2'}
            />
            <div style={{ height: 20 }} />
            <FResourceTypeInput4
              value={$resourceType}
              onChange={(value) => {
                set$resourceType(value);
                set$resourceType_errorText(
                  value === null
                    ? FI18n.i18nNext.tAuto('naming_convention_resource_type_required')
                    : '',
                );
                set$dataIsDirty_count(get$dataIsDirty_count() + 1);
              }}
              subjectType={'exhibitCollection'}
              showAddNewType={false}
              placeholder={FI18n.i18nNext.t('createexhibitcollection_input_type_hint')}
            />
            {$resourceType_errorText !== '' && (
              <>
                <div style={{ height: 5 }} />
                <div style={{ color: '#EE4040', fontSize: 12 }}>{$resourceType_errorText}</div>
              </>
            )}
          </div>
          <div style={{ height: 5 }} />
          <div className={styles.block}>
            <FComponentsLib.FContentText
              text={
                // 合集标题
                FI18n.i18nNext.tAuto('createexhibitcollection_input_title_label')
              }
              type={'highlight'}
            />
            <div style={{ height: 5 }} />
            <FComponentsLib.FContentText
              text={
                // 标题直接影响商品的搜索曝光机会，建议在标题中加入品牌/内容主旨，例如，《大明风华：明朝人的城市生活》；标题长度不超过100个字符。
                FI18n.i18nNext.tAuto('createexhibitcollection_input_title_info')
              }
              type={'additional2'}
            />
            <div style={{ height: 20 }} />
            <FInput_PinyinSafeTextCounter
              value={$resourceTitle}
              placeholder={
                // 输入合集标题
                FI18n.i18nNext.t('createexhibitcollection_input_title_hint')
              }
              style={{ width: 880 }}
              onChangeValue={(value: string) => {
                const name: string = value.substring(0, 60);
                if (get$resourceTitle() === get$resourceName() && get$resourceName() !== name) {
                  onChangeName(name.substring(0, 60));
                }
                set$resourceTitle(value);
                set$resourceTitle_errorText(
                  value === ''
                    ? // '请输入标题'
                      FI18n.i18nNext.tAuto('naming_convention_exhcoll_title_required')
                    : '',
                );
                set$dataIsDirty_count(get$dataIsDirty_count() + 1);
              }}
              lengthLimit={100}
              hasError={$resourceTitle_errorText !== ''}
            />
            {$resourceTitle_errorText !== '' && (
              <>
                <div style={{ height: 5 }} />
                <div style={{ color: '#EE4040', fontSize: 12 }}>{$resourceTitle_errorText}</div>
              </>
            )}
          </div>
          <div style={{ height: 5 }} />
          <div className={styles.block}>
            <FComponentsLib.FContentText
              text={
                // 合集授权标识
                FI18n.i18nNext.tAuto('createexhibitcollection_input_authid_label')
              }
              type={'highlight'}
            />
            <div style={{ height: 5 }} />
            <FComponentsLib.FContentText
              text={
                // 此合集在整个授权系统中的唯一标识符，一旦创建则不能更改。
                FI18n.i18nNext.tAuto('createexhibitcollection_input_authid_info')
              }
              type={'additional2'}
            />
            <div style={{ height: 20 }} />
            <div className={styles.resourceName}>
              <FComponentsLib.FContentText
                text={$nodeInfo?.nodeName || ''}
                style={{
                  lineHeight: '38px',
                  flexShrink: 0,
                  display: 'inline-block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 230,
                }}
              />
              <FComponentsLib.FContentText
                text={'/'}
                style={{
                  lineHeight: '38px',
                  flexShrink: 0,
                  display: 'inline-block',
                }}
              />
              <div>
                <FInput_PinyinSafeTextCounter
                  value={$resourceName}
                  placeholder={
                    // 输入合集授权标识
                    FI18n.i18nNext.t('createexhibitcollection_input_authid_hint')
                  }
                  onChangeValue={(value) => {
                    onChangeName(value);
                    set$dataIsDirty_count(get$dataIsDirty_count() + 1);
                  }}
                  lengthLimit={60}
                  style={{ width: 600 }}
                  hasError={$resourceName_errorText !== ''}
                />

                {$resourceName_errorText === '' && $resourceName !== $resourceName_optimized && (
                  <>
                    <div style={{ height: 5 }} />
                    <div>
                      <div
                        style={{
                          color: '#44C28C',
                          fontSize: 12,
                          lineHeight: '18px',
                        }}
                      >
                        {/* 你的展品合集标识将自动转换为 <strong>{$resourceName_optimized}</strong> */}
                        {FI18n.i18nNext.tAuto('add2node_input_exhibit_authid_automodified_msg', {
                          authid: $resourceName_optimized,
                        })}
                      </div>
                      <div style={{ height: 5 }} />
                      <div
                        style={{
                          color: '#E9A923',
                          fontSize: 12,
                          lineHeight: '18px',
                        }}
                      >
                        {/* {String.raw`展品合集授权标识长度必须在 1–60 字符之间，不能包含空格、表情符号（emoji）及以下字符：\ / : * ? " < > | @ # $`} */}
                        {FI18n.i18nNext.tAuto('naming_convention_exhibits_name')}
                      </div>
                    </div>
                  </>
                )}

                {$resourceName_errorText !== '' && (
                  <>
                    <div style={{ height: 5 }} />
                    <div style={{ color: '#EE4040', fontSize: 12 }}>{$resourceName_errorText}</div>
                  </>
                )}
              </div>
              {$resourceName_isVerify && (
                <FIcons.FLoading style={{ lineHeight: '38px', flexShrink: 0 }} />
              )}

              {!$resourceName_isVerify &&
                $resourceName !== '' &&
                $resourceName_errorText === '' && (
                  <FIcons.FCheck style={{ lineHeight: '38px', flexShrink: 0 }} />
                )}
            </div>
          </div>

          <div style={{ height: 30 }} />

          <div className={styles.btn}>
            <FHotspotTooltip
              id={'collectionCreator.step1.createButton'}
              style={{ left: 32, bottom: -38 }}
              text={FI18n.i18nNext.tAuto('hotpots_cqr_createauthid')}
              onOpenChange={(o) => {
                if (o) {
                  fSetHotspotTooltipVisible('collectionCreator.step1.createButton', {
                    value: false,
                    effectiveImmediately: false,
                    onlyNullish: false,
                  });
                }
              }}
            >
              <FComponentsLib.FRectBtn
                disabled={
                  $resourceType === null ||
                  $resourceName === '' ||
                  $resourceName_errorText !== '' ||
                  $resourceName_isVerify ||
                  $resourceTitle === '' ||
                  $resourceTitle_errorText !== ''
                }
                type={'primary'}
                onClick={async () => {
                  const {
                    ret,
                    errCode,
                    msg,
                    data,
                  }: {
                    ret: number;
                    errCode: number;
                    msg: string;
                    data: {
                      presentableId: string;
                    };
                  } = await FServiceAPI.Exhibit.createPresentableCollection({
                    nodeId: Number(params.nodeID!),
                    presentableName: get$resourceName_optimized(),
                    presentableTitle: get$resourceTitle(),
                    resourceTypeCode: $resourceType?.value || '',
                  });
                  if (ret !== 0 || errCode !== 0) {
                    fMessage(msg);
                    return;
                  }
                  set$dataIsDirty_count(0);
                  setTimeout(() => {
                    history.replace(`/node/createdCollectionExhibit/formal/${data.presentableId}`);
                  }, 10);
                }}
              >
                {
                  // 立即创建
                  FI18n.i18nNext.tAuto('createexhibitcollection_btn_submit')
                }
              </FComponentsLib.FRectBtn>
            </FHotspotTooltip>
          </div>
        </div>
      </div>
    </>
  );
}

export default Creator;
